import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit, Loader2, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";
import { toast } from "sonner";
import AdminSearchBar from "../../components/AdminSearchBar";
import CustomerMobileVisibility from "./_partials/CustomerMobileVisibility";

export default function AdminOwners() {
    const [rows, setRows] = useState([]);
    const [rests, setRests] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ mobile_number: "", first_name: "", email: "", restaurant_ids: [] });
    const [query, setQuery] = useState("");

    const load = () => api.get("/admin/owners").then(({ data }) => setRows(data));
    useEffect(() => { load(); api.get("/admin/restaurants").then(({ data }) => setRests(data)); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((o) => {
            const shops = (o.restaurant_names || []).join(" ");
            const hay = `${o.first_name || ""} ${o.last_name || ""} ${o.mobile_number || ""} ${o.email || ""} ${o.status || ""} ${shops}`.toLowerCase();
            return hay.includes(q);
        });
    }, [rows, query]);

    const startNew = () => { setForm({ mobile_number: "", first_name: "", email: "", restaurant_ids: [] }); setEditing("new"); };
    const startEdit = (o) => { setForm({ ...o }); setEditing(o); };

    const save = async () => {
        if (!/^\d{10}$/.test(form.mobile_number || "")) return toast.error("Valid 10-digit mobile required");
        setBusy(true);
        try {
            if (editing === "new") {
                await api.post("/admin/owners", form);
                toast.success("Owner invited");
            } else {
                await api.patch(`/admin/owners/${editing.id}`, form);
                toast.success("Owner updated");
            }
            setEditing(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const remove = async (uid) => {
        if (!window.confirm("Delete this owner? Past orders will keep their reference.")) return;
        try {
            const { data } = await api.delete(`/admin/owners/${uid}`);
            toast.success(data?.mode === "archived"
                ? `Archived — ${data.orders} past order(s) kept`
                : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const impersonate = async (o) => {
        // Open the popup synchronously to preserve the user gesture
        // (otherwise the post-await window.open() is blocked).
        const win = window.open("about:blank", "_blank");
        try {
            const { data } = await api.post(`/admin/owners/${o.id}/impersonate`);
            const url = new URL(window.location.origin);
            url.searchParams.set("tenant", data.tenant.slug);
            url.searchParams.set("impersonate", data.token);
            url.pathname = "/merchant";
            if (win) {
                win.location.replace(url.toString());
            } else {
                window.open(url.toString(), "_blank", "noopener");
            }
            toast.success(`Opening preview as ${o.first_name || "owner"}`);
        } catch (e) {
            if (win) win.close();
            toast.error(e?.response?.data?.detail || "Could not impersonate");
        }
    };

    const toggleRest = (id) => {
        const has = form.restaurant_ids?.includes(id);
        setForm({ ...form, restaurant_ids: has ? form.restaurant_ids.filter((x) => x !== id) : [...(form.restaurant_ids || []), id] });
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h1 className="text-2xl font-bold">Shop Owners</h1><p className="text-sm text-muted-foreground mt-1">{rows.length} owners{query ? ` · ${filtered.length} match` : ""}. They log in with their mobile + OTP and see only their shops.</p></div>
                <button data-testid="add-owner" onClick={startNew} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Invite owner</button>
            </div>
            <CustomerMobileVisibility />
            <div className="mt-4">
                <AdminSearchBar value={query} onChange={setQuery} placeholder="Search by name, mobile, shop, status…" testid="owners-search" count={filtered.length} />
            </div>
            <div className="mt-4 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead><tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider"><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">Shops</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
                    <tbody>
                        {filtered.map((o) => (
                            <tr key={o.id} data-testid={`owner-row-${o.id}`} className="border-t border-border">
                                <td className="px-4 py-3 font-semibold">{o.first_name} {o.last_name || ""}</td>
                                <td className="px-4 py-3">+91 {o.mobile_number}</td>
                                <td className="px-4 py-3 text-xs text-muted-foreground">{(o.restaurant_names || o.restaurant_ids || []).join(", ") || <span className="italic">none yet</span>}</td>
                                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${o.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{o.status}</span></td>
                                <td className="px-4 py-3 text-right">
                                    <button data-testid={`impersonate-${o.id}`} onClick={() => impersonate(o)} className="text-xs text-sky-600 mr-3 inline-flex items-center gap-1" title="Open merchant dashboard as this owner"><Eye size={14} /> Impersonate</button>
                                    <button data-testid={`edit-${o.id}`} onClick={() => startEdit(o)} className="text-xs text-primary mr-3"><Edit size={14} className="inline" /> Edit</button>
                                    <button data-testid={`del-${o.id}`} onClick={() => remove(o.id)} className="text-xs text-destructive"><Trash2 size={14} className="inline" /></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No owners yet</td></tr>}
                    </tbody>
                </table>
            </div>

            {editing && (
                <Modal title={editing === "new" ? "Invite Owner" : "Edit Owner"} onClose={() => setEditing(null)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Mobile"><input data-testid="own-mobile" inputMode="numeric" maxLength={10} disabled={editing !== "new"} className="inp" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value.replace(/\D/g, "") })} /></Field>
                        <Field label="First name"><input className="inp" value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
                        <Field full label="Email (optional)"><input className="inp" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                        <Field full label="Assign restaurants">
                            <div className="grid grid-cols-2 gap-2 mt-1 max-h-48 overflow-y-auto p-2 bg-muted/40 rounded-xl">
                                {rests.map((r) => {
                                    const checked = form.restaurant_ids?.includes(r.id);
                                    return (
                                        <label key={r.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-sm ${checked ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                                            <input data-testid={`assign-${r.id}`} type="checkbox" checked={!!checked} onChange={() => toggleRest(r.id)} className="accent-primary" />
                                            <span className="font-semibold truncate">{r.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </Field>
                    </div>
                    <button data-testid="own-save" disabled={busy} onClick={save} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />} {editing === "new" ? "Invite" : "Save"}</button>
                </Modal>
            )}
        </div>
    );
}
